import {
  Box,
  Button,
  Divider,
  Heading,
  HStack,
  SimpleGrid,
  Text,
  useDisclosure,
  useToast,
  VStack
} from '@chakra-ui/react'
import {
  AlmaIcon,
  GCalIcon,
  HeadwayIcon,
  ICalIcon,
  OutlookIcon,
  SimplePracticeIcon,
  TherapyNotesIcon
} from '@icons'
import React from 'react'
import {
  useClinicianSchedulingControllerCreateExternalCalendar,
  useClinicianSchedulingControllerDeleteExternalCalendar,
  useClinicianSchedulingControllerGetExternalCalendars
} from '~/clinician-api'
import type { ExternalCalendarResponseDto } from '~/clinician-api/models'
import CalendarConnectModal from './calendar-connect-modal'
import { calendarInstructions } from './calendar-instructions'
import CalendarSyncTile from './calendar-sync-tile'
import DisconnectCalendarModal from './disconnect-calendar-modal'
import SyncBlueprintCalendar from './sync-blueprint-calendar'

const CalendarSync = () => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const toast = useToast()
  const [selectedCalendar, setSelectedCalendar] = React.useState<{
    icon: React.ReactNode
    title: string
    type: string
    instructions: string
  } | null>(null)
  const [disconnectingCalendar, setDisconnectingCalendar] =
    React.useState<ExternalCalendarResponseDto | null>(null)
  const [isDisconnectModalOpen, setDisconnectModalOpen] = React.useState(false)

  const { data: connectedCalendars = [], refetch } =
    useClinicianSchedulingControllerGetExternalCalendars()

  const { mutateAsync: createExternalCalendar, isLoading } =
    useClinicianSchedulingControllerCreateExternalCalendar({
      mutation: {
        onSuccess: () => {
          toast({
            title: 'Calendar connected',
            status: 'success',
            duration: 5000,
            isClosable: true
          })
          onClose()
          setSelectedCalendar(null)
          refetch()
        },
        onError: (error: unknown) => {
          toast({
            title: 'Failed to connect calendar',
            status: 'error',
            duration: 5000,
            isClosable: true
          })
        }
      }
    })

  const { mutateAsync: deleteExternalCalendar, isLoading: isDeleting } =
    useClinicianSchedulingControllerDeleteExternalCalendar({
      mutation: {
        onSuccess: () => {
          toast({
            title: 'Calendar disconnected',
            status: 'success',
            duration: 5000,
            isClosable: true
          })
          setDisconnectingCalendar(null)
          setDisconnectModalOpen(false)
          refetch()
        }
      }
    })

  const calendarOptions = [
    {
      icon: <GCalIcon />,
      title: 'Google Calendar',
      type: 'google',
      instructions: calendarInstructions.googleCalendar
    },
    {
      icon: <OutlookIcon />,
      title: 'Outlook',
      type: 'outlook',
      instructions: calendarInstructions.outlook
    },
    {
      icon: <ICalIcon />,
      title: 'iCal',
      type: 'ical',
      instructions: calendarInstructions.iCal
    },
    {
      icon: <SimplePracticeIcon />,
      title: 'Simple Practice',
      type: 'simplepractice',
      instructions: calendarInstructions.simplePractice
    },
    {
      icon: <HeadwayIcon />,
      title: 'Headway',
      type: 'headway',
      instructions: calendarInstructions.headway
    },
    {
      icon: <AlmaIcon />,
      title: 'Alma',
      type: 'alma',
      instructions: calendarInstructions.alma
    },
    {
      icon: <TherapyNotesIcon />,
      title: 'TherapyNotes',
      type: 'therapynotes',
      instructions: calendarInstructions.therapyNotes
    }
  ]

  const getCalendarIcon = (type: string, width?: number, height?: number) => {
    const option = calendarOptions.find(opt => opt.type === type)
    return option?.icon || <ICalIcon width={width} height={height} />
  }

  const getCalendarTitle = (type: string) => {
    const option = calendarOptions.find(opt => opt.type === type)
    return option?.title || type
  }

  const handleCalendarClick = (calendar: (typeof calendarOptions)[0]) => {
    setSelectedCalendar(calendar)
    onOpen()
  }

  const handleSaveCalendar = async (url: string, name?: string) => {
    if (!selectedCalendar) return

    await createExternalCalendar({
      data: {
        type: selectedCalendar.type,
        name: name,
        icsUrl: url
      }
    })
  }

  const openDisconnectModal = (calendar: ExternalCalendarResponseDto) => {
    setDisconnectingCalendar(calendar)
    setDisconnectModalOpen(true)
  }

  const closeDisconnectModal = () => {
    setDisconnectingCalendar(null)
    setDisconnectModalOpen(false)
  }

  const handleConfirmDisconnect = async () => {
    if (disconnectingCalendar?.id) {
      await deleteExternalCalendar({
        externalCalendarId: disconnectingCalendar.id
      })
    } else {
      setDisconnectingCalendar(null)
      setDisconnectModalOpen(false)
    }
  }

  return (
    <VStack spacing={8} align="stretch" pb="100px">
      <Box>
        <Box mb={'medium'} w={'100%'}>
          <Heading mb="xxsmall">External Calendars</Heading>
          <Text>
            Connect your external calendars with Blueprint to stay accurate and
            aligned with everything you have going on.
          </Text>
        </Box>

        {connectedCalendars.length > 0 && (
          <Box mb="32px">
            <Heading size="small" mb={4}>
              Connected
            </Heading>
            <Box
              border="1px solid"
              borderColor="gray.200"
              borderRadius="8px"
              overflow="hidden"
            >
              {connectedCalendars.map(
                (calendar: ExternalCalendarResponseDto, index: number) => (
                  <HStack
                    key={calendar.id}
                    spacing={0}
                    borderBottom={
                      index < connectedCalendars.length - 1
                        ? '1px solid'
                        : 'none'
                    }
                    borderBottomColor="gray.200"
                  >
                    <Box
                      p={4}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      w="64px"
                      h="64px"
                    >
                      <Box
                        w="32px"
                        h="32px"
                        p="8px"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        borderRadius="8px"
                        border="1px solid"
                        borderColor="rgba(40, 40, 40, 0.05)"
                        bg="#F9F9F9"
                      >
                        {getCalendarIcon(calendar.type, 16, 16)}
                      </Box>
                    </Box>
                    <Box
                      p={4}
                      flex={1}
                      display="flex"
                      alignItems="center"
                      minWidth={0}
                    >
                      <HStack spacing={2} align="center" minWidth={0}>
                        <Text
                          fontWeight="medium"
                          noOfLines={1}
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                          flex={1}
                          minWidth={0}
                        >
                          {calendar.name || getCalendarTitle(calendar.type)}
                        </Text>
                        {calendar.name && (
                          <Text
                            fontSize="sm"
                            color="gray.600"
                            noOfLines={1}
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                            flexShrink={1}
                            minWidth={0}
                          >
                            {getCalendarTitle(calendar.type)}
                          </Text>
                        )}
                      </HStack>
                    </Box>
                    <Box
                      p={4}
                      display="flex"
                      alignItems="center"
                      flexShrink={0}
                    >
                      <Button
                        variant="outline"
                        size="md"
                        colorScheme="gray"
                        borderRadius="8px"
                        onClick={() => openDisconnectModal(calendar)}
                        _hover={{ bg: 'rgba(45, 84, 232, 0.04)' }}
                      >
                        Disconnect
                      </Button>
                    </Box>
                  </HStack>
                )
              )}
            </Box>
          </Box>
        )}

        <Heading size="small">Add external calendar</Heading>
        <SimpleGrid
          columns={{ base: 1, md: 2, lg: 3, xl: 4 }}
          spacing={4}
          data-testid="external-calendars"
          my="16px"
          w="100%"
          mb="24px"
        >
          {calendarOptions.map(calendar => (
            <CalendarSyncTile
              key={calendar.title}
              icon={calendar.icon}
              title={calendar.title}
              onClick={() => handleCalendarClick(calendar)}
            />
          ))}
        </SimpleGrid>

        <Text mb="40px">
          Once connected, your calendar will sync with Blueprint. Edits can only
          be made in your original calendar app.
          <br />
          <b>Just a heads-up:</b> it may take up to 15 minutes for updates to
          show.
        </Text>

        <SyncBlueprintCalendar />

        {selectedCalendar && (
          <CalendarConnectModal
            isOpen={isOpen}
            onClose={onClose}
            calendarName={selectedCalendar.title}
            icon={selectedCalendar.icon}
            instructions={selectedCalendar.instructions}
            onSave={handleSaveCalendar}
            isLoading={isLoading}
          />
        )}

        <DisconnectCalendarModal
          isOpen={isDisconnectModalOpen}
          onClose={closeDisconnectModal}
          onConfirm={handleConfirmDisconnect}
          isLoading={isDeleting}
        />
      </Box>
    </VStack>
  )
}

export default CalendarSync
